import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config/api';

const formatMoney = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;

const getMonthRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;
    return { dateFrom, dateTo };
};

function CashJournalPanel({ token }) {
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [days, setDays] = useState([]);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [incomeCategories, setIncomeCategories] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMovementOpen, setEditMovementOpen] = useState(false);
    const [editingMovement, setEditingMovement] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dayForm, setDayForm] = useState({
        opening_balance: '',
        closing_balance: '',
        employee_id: '',
        transfer_comment: '',
        notes: ''
    });
    const [movementForm, setMovementForm] = useState({
        direction: 'in',
        amount: '',
        category: 'прочее',
        description: ''
    });
    const [editForm, setEditForm] = useState({
        amount: '',
        category: '',
        description: ''
    });
    const [suggestedOpening, setSuggestedOpening] = useState(null);
    const [loadError, setLoadError] = useState('');

    const monthRange = useMemo(() => getMonthRange(currentMonth), [currentMonth]);
    const monthLabel = currentMonth.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });

    const apiFetch = (url, options = {}) => fetch(url, {
        ...options,
        headers: {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`
        }
    });

    const loadJournal = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const params = new URLSearchParams({
                date_from: monthRange.dateFrom,
                date_to: monthRange.dateTo
            });
            const response = await apiFetch(`${API_URL}/api/cash-journal?${params.toString()}`);
            const data = await response.json();
            if (data.success) {
                setDays(data.days || []);
                setExpenseCategories(data.expense_categories || []);
                setIncomeCategories(data.income_categories || []);
                if (!(data.days || []).length) {
                    setLoadError('За этот месяц дни не создались. Обновите страницу или переключите месяц стрелками.');
                }
            } else {
                setLoadError(data.error || 'Не удалось загрузить кассовый журнал');
            }
        } catch (error) {
            console.error('Error loading cash journal:', error);
            setLoadError('Не удалось загрузить кассовый журнал');
        } finally {
            setLoading(false);
        }
    };

    const loadEmployees = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/employees`);
            const data = await response.json();
            if (data.success) {
                setEmployees(data.employees || data.data || []);
            }
        } catch (error) {
            console.error('Error loading employees:', error);
        }
    };

    useEffect(() => {
        loadJournal();
    }, [currentMonth, token]);

    useEffect(() => {
        loadEmployees();
    }, [token]);

    const reloadSelectedDay = async (journalDate) => {
        const response = await apiFetch(`${API_URL}/api/cash-journal/${journalDate}`);
        const data = await response.json();
        if (data.success) {
            const day = data.day;
            setSelectedDay(day);
            setSuggestedOpening(data.suggested_opening);
            setDayForm({
                opening_balance: day.opening_balance ?? '',
                closing_balance: day.closing_balance ?? '',
                employee_id: day.employee_id || '',
                transfer_comment: day.transfer_comment || '',
                notes: day.notes || ''
            });
            if (data.expense_categories?.length) setExpenseCategories(data.expense_categories);
            if (data.income_categories?.length) setIncomeCategories(data.income_categories);
        }
    };

    const openDayDialog = async (day) => {
        setDialogOpen(true);
        setMovementForm({ direction: 'in', amount: '', category: 'прочее', description: '' });
        try {
            await reloadSelectedDay(day.journal_date);
        } catch (error) {
            console.error('Error loading day details:', error);
        }
    };

    const handleSaveDay = async () => {
        if (!selectedDay) return;
        setSaving(true);
        try {
            const response = await apiFetch(`${API_URL}/api/cash-journal/${selectedDay.journal_date}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    employee_id: dayForm.employee_id || null,
                    opening_balance: dayForm.opening_balance === '' ? 0 : Number(dayForm.opening_balance),
                    closing_balance: dayForm.closing_balance === '' ? null : Number(dayForm.closing_balance),
                    transfer_comment: dayForm.transfer_comment,
                    notes: dayForm.notes
                })
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось сохранить день');
                return;
            }
            setDialogOpen(false);
            await loadJournal();
        } catch (error) {
            console.error('Error saving day:', error);
            alert('Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const handleAddMovement = async () => {
        if (!selectedDay) return;
        const amount = Number(movementForm.amount);
        if (!amount || amount <= 0) {
            alert('Укажите сумму');
            return;
        }
        try {
            const response = await apiFetch(`${API_URL}/api/cash-journal/${selectedDay.journal_date}/movements`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(movementForm)
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось добавить операцию');
                return;
            }
            setMovementForm((prev) => ({ ...prev, amount: '', description: '' }));
            await reloadSelectedDay(selectedDay.journal_date);
            await loadJournal();
        } catch (error) {
            console.error('Error adding movement:', error);
            alert('Ошибка добавления операции');
        }
    };

    const openEditMovement = (movement) => {
        setEditingMovement(movement);
        setEditForm({
            amount: movement.amount,
            category: movement.category,
            description: movement.description || ''
        });
        setEditMovementOpen(true);
    };

    const handleSaveMovement = async () => {
        if (!editingMovement || !selectedDay) return;
        try {
            const response = await apiFetch(`${API_URL}/api/cash-journal/movements/${editingMovement.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: Number(editForm.amount),
                    category: editForm.category,
                    description: editForm.description
                })
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось сохранить');
                return;
            }
            setEditMovementOpen(false);
            setEditingMovement(null);
            await reloadSelectedDay(selectedDay.journal_date);
            await loadJournal();
        } catch (error) {
            console.error('Error updating movement:', error);
            alert('Ошибка сохранения операции');
        }
    };

    const handleDeleteMovement = async (id) => {
        if (!window.confirm('Удалить эту операцию из журнала?')) return;
        try {
            const response = await apiFetch(`${API_URL}/api/cash-journal/movements/${id}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось удалить');
                return;
            }
            if (selectedDay) {
                await reloadSelectedDay(selectedDay.journal_date);
            }
            await loadJournal();
        } catch (error) {
            console.error('Error deleting movement:', error);
        }
    };

    const applySuggestedOpening = () => {
        if (suggestedOpening != null) {
            setDayForm((prev) => ({ ...prev, opening_balance: suggestedOpening }));
        }
    };

    const dialogIncome = selectedDay?.income_total ?? 0;
    const dialogExpense = selectedDay?.expense_total ?? 0;
    const dialogCalculatedClosing = Number(dayForm.opening_balance || 0) + dialogIncome - dialogExpense;
    const dialogControl = dayForm.closing_balance !== '' && dayForm.closing_balance != null
        ? Number(dayForm.closing_balance) - dialogCalculatedClosing
        : null;

    const categoryOptions = movementForm.direction === 'in' ? incomeCategories : expenseCategories;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h4">Кассовый журнал</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Все дни выбранного месяца. Стрелками можно открыть сентябрь и любой другой месяц — строки создаются автоматически.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center', textTransform: 'capitalize' }}>
                        {monthLabel}
                    </Typography>
                    <IconButton onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                Формула: <strong>начало + приходы (нал) − расходы = должно быть в кассе</strong>.
                Яндекс и безнал в журнал не попадают — только явные наличные продажи.
            </Alert>
            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell>Дата</TableCell>
                                <TableCell>День</TableCell>
                                <TableCell>Администратор</TableCell>
                                <TableCell align="right">В кассе начало</TableCell>
                                <TableCell align="right">Расход</TableCell>
                                <TableCell>Расходы</TableCell>
                                <TableCell align="right">Приход НАЛ</TableCell>
                                <TableCell>Приходы</TableCell>
                                <TableCell align="right">Должно быть</TableCell>
                                <TableCell align="right">В кассе конец</TableCell>
                                <TableCell align="right">Контроль</TableCell>
                                <TableCell align="center"> </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {days.map((day) => (
                                <TableRow
                                    key={day.journal_date}
                                    hover
                                    sx={{
                                        bgcolor: day.is_weekend ? '#fff3e0' : 'inherit',
                                        ...(Math.abs(day.control || 0) > 0.01 && day.closing_balance != null
                                            ? { '& td': { bgcolor: '#ffebee' } }
                                            : {})
                                    }}
                                >
                                    <TableCell>{new Date(`${day.journal_date}T12:00:00`).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}</TableCell>
                                    <TableCell>{day.day_of_week}</TableCell>
                                    <TableCell>{day.employee_name || '—'}</TableCell>
                                    <TableCell align="right">{formatMoney(day.opening_balance)}</TableCell>
                                    <TableCell align="right">{day.expense_total > 0 ? formatMoney(day.expense_total) : '—'}</TableCell>
                                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {day.disbursements_summary || '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatMoney(day.income_total)}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            б {formatMoney(day.cash_tickets)} / к {formatMoney(day.cash_cafe)} / с {formatMoney(day.cash_souvenirs)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {day.income_summary || '—'}
                                    </TableCell>
                                    <TableCell align="right">{formatMoney(day.calculated_closing)}</TableCell>
                                    <TableCell align="right">{day.closing_balance != null ? formatMoney(day.closing_balance) : '—'}</TableCell>
                                    <TableCell align="right">
                                        {day.control != null ? (
                                            <Chip
                                                size="small"
                                                label={formatMoney(day.control)}
                                                color={Math.abs(day.control) < 0.01 ? 'success' : 'error'}
                                            />
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => openDayDialog(day)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Касса за {selectedDay && new Date(`${selectedDay.journal_date}T12:00:00`).toLocaleDateString('ru-RU')}
                </DialogTitle>
                <DialogContent>
                    {selectedDay && (
                        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                <Typography variant="body2">Приходы (нал): <strong>{formatMoney(dialogIncome)}</strong></Typography>
                                <Typography variant="body2">Расходы: <strong>{formatMoney(dialogExpense)}</strong></Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    В операциях — только наличные продажи из CRM и ручные записи.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Должно быть в кассе: <strong>{formatMoney(dialogCalculatedClosing)}</strong>
                                </Typography>
                                {dialogControl != null && (
                                    <Typography variant="body2" color={Math.abs(dialogControl) < 0.01 ? 'success.main' : 'error.main'}>
                                        Контроль: {formatMoney(dialogControl)}
                                    </Typography>
                                )}
                            </Paper>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Администратор</InputLabel>
                                    <Select
                                        value={dayForm.employee_id}
                                        label="Администратор"
                                        onChange={(e) => setDayForm({ ...dayForm, employee_id: e.target.value })}
                                    >
                                        <MenuItem value="">Не указан</MenuItem>
                                        {employees.map((employee) => (
                                            <MenuItem key={employee.id} value={employee.id}>
                                                {employee.full_name || employee.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="В кассе на начало дня"
                                        value={dayForm.opening_balance}
                                        onChange={(e) => setDayForm({ ...dayForm, opening_balance: e.target.value })}
                                    />
                                    {suggestedOpening != null && (
                                        <Button size="small" sx={{ mt: 0.5, whiteSpace: 'nowrap' }} onClick={applySuggestedOpening}>
                                            ← {formatMoney(suggestedOpening)}
                                        </Button>
                                    )}
                                </Box>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="В кассе на конец дня (факт)"
                                    value={dayForm.closing_balance}
                                    onChange={(e) => setDayForm({ ...dayForm, closing_balance: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Комментарий к переводу"
                                    value={dayForm.transfer_comment}
                                    onChange={(e) => setDayForm({ ...dayForm, transfer_comment: e.target.value })}
                                />
                            </Box>

                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Операции по кассе</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Тип</TableCell>
                                            <TableCell>Категория</TableCell>
                                            <TableCell>Описание</TableCell>
                                            <TableCell>Источник</TableCell>
                                            <TableCell align="right">Сумма</TableCell>
                                            <TableCell align="center"> </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(selectedDay.movements || []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                    Нет операций за этот день
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            (selectedDay.movements || []).map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={item.direction === 'in' ? 'Приход нал' : 'Расход'}
                                                            color={item.direction === 'in' ? 'success' : 'warning'}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{item.category}</TableCell>
                                                    <TableCell>{item.description || '—'}</TableCell>
                                                    <TableCell>
                                                        {item.is_auto ? (
                                                            <Chip size="small" label="CRM" color="primary" variant="outlined" />
                                                        ) : (
                                                            <Chip size="small" label="Вручную" variant="outlined" />
                                                        )}
                                                        {item.is_override && (
                                                            <Chip size="small" label="изм." sx={{ ml: 0.5 }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">{formatMoney(item.amount)}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                        <IconButton size="small" onClick={() => openEditMovement(item)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteMovement(item.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="subtitle2">Добавить операцию</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr 1fr 2fr auto' }, gap: 1, alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Тип</InputLabel>
                                    <Select
                                        value={movementForm.direction}
                                        label="Тип"
                                        onChange={(e) => setMovementForm({
                                            direction: e.target.value,
                                            amount: movementForm.amount,
                                            category: e.target.value === 'in' ? 'прочее' : 'прочее',
                                            description: movementForm.description
                                        })}
                                    >
                                        <MenuItem value="in">Приход</MenuItem>
                                        <MenuItem value="out">Расход</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Сумма"
                                    value={movementForm.amount}
                                    onChange={(e) => setMovementForm({ ...movementForm, amount: e.target.value })}
                                />
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Категория</InputLabel>
                                    <Select
                                        value={movementForm.category}
                                        label="Категория"
                                        onChange={(e) => setMovementForm({ ...movementForm, category: e.target.value })}
                                    >
                                        {categoryOptions.map((category) => (
                                            <MenuItem key={category} value={category}>{category}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Комментарий"
                                    value={movementForm.description}
                                    onChange={(e) => setMovementForm({ ...movementForm, description: e.target.value })}
                                />
                                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddMovement}>
                                    Добавить
                                </Button>
                            </Box>

                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Заметки за день"
                                value={dayForm.notes}
                                onChange={(e) => setDayForm({ ...dayForm, notes: e.target.value })}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
                    <Button variant="contained" onClick={handleSaveDay} disabled={saving}>
                        {saving ? 'Сохранение…' : 'Сохранить день'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editMovementOpen} onClose={() => setEditMovementOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Редактировать операцию</DialogTitle>
                <DialogContent>
                    {editingMovement && (
                        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {editingMovement.is_auto && (
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                    Операция из CRM. После правки она не будет перезаписываться при синхронизации.
                                </Alert>
                            )}
                            <TextField
                                fullWidth
                                type="number"
                                label="Сумма"
                                value={editForm.amount}
                                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Категория</InputLabel>
                                <Select
                                    value={editForm.category}
                                    label="Категория"
                                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                                >
                                    {(editingMovement.direction === 'in' ? incomeCategories : expenseCategories).map((category) => (
                                        <MenuItem key={category} value={category}>{category}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Описание"
                                value={editForm.description}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditMovementOpen(false)}>Отмена</Button>
                    <Button variant="contained" onClick={handleSaveMovement}>Сохранить</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default CashJournalPanel;
